/**
 * <jgd-shop-rating> — zeigt die Shop-Gesamtbewertung (Judge.me) als Sterne.
 *
 * FALLBACK-PFAD. Primär rendert blocks/jgd-shop-rating.liquid den Block
 * komplett serverseitig aus den Judge.me-Shop-Metafeldern (data-rendered,
 * Modul wird dann gar nicht erst geladen). Dieses Element greift nur, wenn
 * die Metafelder leer sind (Store ohne Judge.me-Daten), und holt das
 * Aggregat über die Judge.me Widget-API (Public Token — nur die
 * /widgets/*-Endpunkte erlauben den Public Token und senden CORS-Header;
 * die übrige REST-API verlangt den privaten Token und blockt Browser):
 *   GET /api/v1/widgets/all_reviews_rating → {"all_reviews_rating":"4.33"}
 *   GET /api/v1/widgets/all_reviews_count  → {"all_reviews_count":3}
 *
 * Das Ergebnis wird 24 h in localStorage gecacht (max. ein API-Call pro
 * Besucher und Tag; Wiederbesucher rendern sofort aus dem Cache). Ohne
 * Token/Daten bleibt das Element hidden; im Theme-Editor (data-design-mode)
 * zeigt es ohne Token Beispielwerte, damit es stylbar bleibt.
 */

const API_BASE = 'https://api.judge.me/api/v1';
const CACHE_KEY = 'jgd-shop-rating';
const CACHE_TTL = 24 * 60 * 60 * 1000;

class JgdShopRating extends HTMLElement {
  connectedCallback() {
    this.#init();
  }

  async #init() {
    // Liquid hat bereits aus den Shop-Metafeldern gerendert — nichts zu tun.
    if (this.hasAttribute('data-rendered')) return;

    const { apiToken, shopDomain } = this.dataset;

    if (!apiToken) {
      // Editor-Vorschau ohne Token: Beispielwerte, damit der Block stylbar ist
      if (this.hasAttribute('data-design-mode')) this.#render({ rating: 4.8, count: 1234 });
      return;
    }

    const cached = this.#readCache();
    if (cached) return this.#render(cached);

    try {
      const data = await this.#fetchRating(apiToken, shopDomain);
      if (!data) return;
      this.#writeCache(data);
      this.#render(data);
    } catch {
      // Netzwerk-/API-Fehler: Block bleibt einfach ausgeblendet
    }
  }

  #readCache() {
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY));
      if (cached && Date.now() - cached.ts < CACHE_TTL && cached.rating > 0) return cached;
    } catch {
      // kaputter/blockierter Storage → einfach frisch laden
    }
    return null;
  }

  /** @param {{rating: number, count: number}} data */
  #writeCache(data) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ ...data, ts: Date.now() }));
    } catch {
      // Storage voll/blockiert — Cache ist verzichtbar
    }
  }

  /**
   * @returns {Promise<{rating: number, count: number} | null>}
   */
  async #fetchRating(apiToken, shopDomain) {
    const auth = `api_token=${encodeURIComponent(apiToken)}&shop_domain=${encodeURIComponent(shopDomain)}`;

    const [ratingResponse, countResponse] = await Promise.all([
      fetch(`${API_BASE}/widgets/all_reviews_rating?${auth}`),
      fetch(`${API_BASE}/widgets/all_reviews_count?${auth}`),
    ]);
    if (!ratingResponse.ok) return null;

    const rating = Number.parseFloat((await ratingResponse.json())?.all_reviews_rating);
    if (!Number.isFinite(rating) || rating <= 0) return null;

    let count = 0;
    if (countResponse.ok) {
      const parsed = Number.parseInt((await countResponse.json())?.all_reviews_count, 10);
      if (Number.isFinite(parsed)) count = parsed;
    }

    return { rating, count };
  }

  /** @param {{rating: number, count: number}} data */
  #render({ rating, count }) {
    const locale = document.documentElement.lang || 'de';
    const ratingText = rating.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    const countText = count.toLocaleString(locale);

    this.querySelector('[ref="starsClip"]')?.style.setProperty('--jgd-rating', String(rating));
    if (this.hasAttribute('data-show-rating')) {
      const value = this.querySelector('[ref="value"]');
      if (value) value.textContent = ratingText;
    }
    if (this.hasAttribute('data-show-count') && count > 0) {
      const countEl = this.querySelector('[ref="count"]');
      if (countEl) countEl.textContent = `(${countText})`;
    }

    // optionaler Zusatzsatz mit [rating]/[count]-Platzhaltern (Block-Setting)
    const sentenceTemplate = this.dataset.sentenceTemplate;
    const sentence = this.querySelector('[ref="sentence"]');
    if (sentenceTemplate && sentence) {
      sentence.textContent = sentenceTemplate.replaceAll('[rating]', ratingText).replaceAll('[count]', countText);
    }

    const inner = this.querySelector('[ref="inner"]');
    if (inner) {
      const label = (this.dataset.ariaTemplate ?? '')
        .replace('[rating]', ratingText)
        .replace('[count]', countText);
      if (label) {
        inner.setAttribute('aria-label', label);
        if (inner.tagName !== 'A') inner.setAttribute('role', 'img');
      }
    }

    this.hidden = false;
  }
}

if (!customElements.get('jgd-shop-rating')) {
  customElements.define('jgd-shop-rating', JgdShopRating);
}
